import * as React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { useTheme } from "./components/common/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/common/ProtectedRoute";
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { IconX } from '@tabler/icons-react';
import './assets/css/global.css'
import Layout from "./components/dashboard/Layout";
import Dashboard from "./pages/dashboard/Dashboard";
import DashboardDetail from "./pages/dashboard/DashboardDetail";
import FileDashboard from "./pages/fileManager/FileDashboard";
import FileDocuments from "./pages/fileManager/FileDocuments";
import FileMedia from "./pages/fileManager/FileMedia";
import FileImages from "./pages/fileManager/FileImages";
import Login from "./pages/auth/Login";
import LandingPage from "./pages/auth/LandingPage";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Register from "./pages/auth/Register";
import BlogDashboard from "./pages/blog/BlogDashboard";
import BlogPost from "./pages/blog/BlogPost";
import BlogList from "./pages/blog/BlogList";
import BlogDetail from "./pages/blog/BlogDetail";
import UiTypography from "./pages/uiElement/UiTypography";
import UiTabs from "./pages/uiElement/UiTabs";
import UiButtons from "./pages/uiElement/UiButtons";
import UiIcons from "./pages/uiElement/UiIcons";
import UiColors from "./pages/uiElement/UiColors";
import UiListGroup from "./pages/uiElement/UiListGroup";
import UiModals from "./pages/uiElement/UiModals";
import UiProgressbars from "./pages/uiElement/UiProgressbars";
import LockScreen from "./pages/auth/LockScreen";
import Page404 from "./pages/auth/Page404";
import BlankPage from "./pages/pages/BlankPage";
import Profile from "./pages/pages/Profile";
import ImageGallery from "./pages/pages/ImageGallery";
import Timeline from "./pages/pages/Timeline";
import Pricing from "./pages/pages/Pricing";
import Invoice from "./pages/pages/Invoice";
import SearchResult from "./pages/pages/SearchResult";
import Faq from "./pages/pages/Faq";
import BasicTable from "./pages/tables/BasicTable";
import NormalTable from "./pages/tables/NormalTable";
import EditableTable from "./pages/tables/EditableTable";
import TableColor from "./pages/tables/TableColor";
import TableFilter from "./pages/tables/TableFilter";
import ApexChart from "./pages/charts/ApexChart";
import Knob from "./pages/charts/Knob";
import JvectorMap from "./pages/maps/JvectorMap";
import YandexMap from "./pages/maps/YandexMap";
import QuillEditor from "./pages/forms/QuillEditor";
import DragDropUpload from "./pages/forms/DragDropUpload";
import Widget from "./pages/widgets/Widget";
import Documentation from "./pages/documentation/Documentation";
import ChangeLog from "./pages/changeLog/ChangeLog";
import FormValidation from "./pages/forms/FormValidation";
import BasicElements from "./pages/forms/BasicElements";
import DataTable from "./pages/tables/DataTable";
import Holiday from "./pages/holiday/Holiday";
import ProjectDashboard from "./pages/projectDashboard/ProjectDashboard";
import Event from "./pages/event/Event";
import Activity from "./pages/activity/Activity";
import Inbox from "./pages/inbox/Inbox";
import Chat from "./pages/chat/Chat";
import Team from "./pages/team/Team";
import Compose from "./pages/inbox/Compose";
import HrSocial from "./pages/hrSocial/HrSocial";
import Employee from "./pages/employees/Employee";
import AddEmployee from "./pages/employees/AddEmployee";
import EmployeeSnapshot from "./pages/employees/EmployeeSnapshot";
import EmployeeDocuments from "./pages/employees/EmployeeDocuments";
import { UploadDocumentPage, EditDocumentPage } from "./pages/employees/EmployeeDocumentUpload";
import ArchivedDocuments from "./pages/employees/ArchivedDocuments";
import PurgeQueue from "./pages/employees/PurgeQueue";
import Leave from "./pages/employees/Leave";
import LeaveTypes from "./pages/employees/LeaveTypes";
import LeaveBalance from "./pages/employees/LeaveBalance";
import MyLeaveBalanceHR from "./pages/employees/MyLeaveBalanceHR";
import LeaveHistory from "./pages/employees/LeaveHistory";
import LeaveApprovals from "./pages/employees/LeaveApprovals";
import TimesheetApprovals from "./pages/employees/TimesheetApprovals";
import TimesheetApprovalDetail from "./pages/employees/TimesheetApprovalDetail";
import LaborTimesheetApprovalDetail from "./pages/employees/LaborTimesheetApprovalDetail";
import HrTimesheetApprovals from "./pages/employees/HrTimesheetApprovals";
import HrTimesheetApprovalDetail from "./pages/employees/HrTimesheetApprovalDetail";
import Attendance from "./pages/employees/Attendance";
import Department from "./pages/employees/Department";
import Payment from "./pages/account/Payment";
import Expense from "./pages/account/Expense";
import AccountInvoice from "./pages/account/Invoice";
import Payslip from "./pages/payroll/Payslip";
import EmployeeSalary from "./pages/payroll/EmployeeSalary";
import PayrollTemplate from "./pages/payroll/PayrollTemplate";
import PayrollRuns from "./pages/payroll/PayrollRuns";
import PayrollRunDetail from "./pages/payroll/PayrollRunDetail";
import PayrollSignature from "./pages/payroll/PayrollSignature";
import ExpenseReport from "./pages/report/ExpenseReport";
import InvoiceReport from "./pages/report/InvoiceReport";
import User from "./pages/user/User";
import AddProject from "./pages/projects/AddProject";
import ProjectList from "./pages/projects/ProjectList";
import ProjectGrid from "./pages/projects/ProjectGrid";
import ProjectDetail from "./pages/projects/ProjectDetail";
import AddClient from "./pages/clients/AddClient";
import ClientList from "./pages/clients/ClientList";
import ClientDetail from "./pages/clients/ClientDetail";
// import TaskBoard from "./pages/taskboard/TaskBoard";
import Ticket from "./pages/ticket/Ticket";
import KPIManagement from "./pages/performance/KPIManagement";
import SelfAppraisal from "./pages/performance/SelfAppraisal";
import PerformanceHistory from "./pages/performance/PerformanceHistory";
import KPIApprovals from "./pages/performance/KPIApprovals";
import Timesheet from "./pages/timesheet/Timesheet";
import Feedback360 from "./pages/performance/Feedback360";
import Feedback360New from "./pages/performance/Feedback360New";
import Feedback360Detail from "./pages/performance/Feedback360Detail";
import Feedback360Respond from "./pages/performance/Feedback360Respond";
import Feedback360HR from "./pages/performance/Feedback360HR";
import Feedback360External from "./pages/performance/Feedback360External";
import SystemSetup from "./pages/admin/SystemSetup";
import OrganizationStructure from "./pages/admin/OrganizationStructure";
import Travel from "./pages/travel/Travel";
import TravelDetail from "./pages/travel/TravelDetail";
import TravelApprovals from "./pages/travel/TravelApprovals";
import VehicleRequests from "./pages/vehicle/VehicleRequests";
import VehicleRequestDetail from "./pages/vehicle/VehicleRequestDetail";
import VehicleApprovals from "./pages/vehicle/VehicleApprovals";
import SupportTickets from "./pages/support/SupportTickets";
import SupportTicketNew from "./pages/support/SupportTicketNew";
import SupportTicketDetail from "./pages/support/SupportTicketDetail";
import SupportQueue from "./pages/support/SupportQueue";
import PayrollApprovals from "./pages/payroll/PayrollApprovals";
import GrantProposal from "./pages/projectManagement/GrantProposal";
import GrantProposalDetail from "./pages/projectManagement/GrantProposalDetail";
import SubmitProposal from "./pages/projectManagement/SubmitProposal";

export default function App() {

  const { selectedOption } = useTheme();

  React.useEffect(() => {
    document.body.setAttribute('lucid-theme', selectedOption);
  }, [selectedOption]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <AuthProvider>
          <Toaster position="top-right" toastOptions={{ duration: 5000 }}>
            {(t) => {
              const richStyles = {
                success: { background: '#f0fdf4', color: '#15803d' },
                error:   { background: '#fef2f2', color: '#dc2626' },
                loading: { background: '#ffffff', color: '#374151' },
              };
              const typeStyle = richStyles[t.type] ?? richStyles.loading;
              return (
                <ToastBar
                  toast={t}
                  style={{
                    ...typeStyle,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    padding: '12px 16px',
                    minWidth: '300px',
                    maxWidth: '420px',
                    overflow: 'visible',
                    position: 'relative',
                  }}
                >
                  {({ icon, message }) => (
                    <>
                      {t.type !== 'loading' && (
                        <button
                          onClick={() => toast.remove(t.id)}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                          style={{
                            position: 'absolute',
                            top: '6px',
                            left: '6px',
                            transform: 'translate(-50%, -50%)',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: typeStyle.background,
                            border: 'none',
                            cursor: 'pointer',
                            color: typeStyle.color,
                            fontSize: '12px',
                            fontWeight: 400,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                            zIndex: 1,
                            transition: 'opacity 0.15s',
                          }}
                        >
                          <IconX size={11} strokeWidth={2.5} />
                        </button>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                        {t.type === 'error' ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#dc2626',
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}>!</span>
                        ) : (
                          icon
                        )}
                        <span style={{ flex: 1, fontSize: '12px', fontWeight: 500, lineHeight: '1.4' }}>{message}</span>
                      </div>
                    </>
                  )}
                </ToastBar>
              );
            }}
          </Toaster>
          <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPage />} />

            {/* Public Auth Routes */}
            <Route path="/auth-login" element={<Login />} />
            {/* Public 360 external rater — no auth required */}
            <Route path="/360-feedback/external" element={<Feedback360External />} />
            <Route path="/auth-forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth-register" element={<Register />} />
            <Route path="/auth-lockscreen" element={<LockScreen />} />

            {/* Protected Dashboard Routes */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>} >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="dashboard/detail/:type" element={<DashboardDetail />} />
              <Route path="file-dashboard" element={<FileDashboard />} />
              <Route path="file-documents" element={<FileDocuments />} />
              <Route path="file-media" element={<FileMedia />} />
              <Route path="file-images" element={<FileImages />} />
              <Route path="blog-dashboard" element={<BlogDashboard />} />
              <Route path="blog-post" element={<BlogPost />} />
              <Route path="blog-list" element={<BlogList />} />
              <Route path="blog-detail" element={<BlogDetail />} />
              <Route path="ui-typography" element={<UiTypography />} />
              <Route path="ui-tabs" element={<UiTabs />} />
              <Route path="ui-buttons" element={<UiButtons />} />
              <Route path="ui-icons" element={<UiIcons />} />
              <Route path="ui-colors" element={<UiColors />} />
              <Route path="ui-list-group" element={<UiListGroup />} />
              <Route path="ui-modals" element={<UiModals />} />
              <Route path="ui-progressbars" element={<UiProgressbars />} />
              <Route path="page-blank" element={<BlankPage />} />
              <Route path="page-profile" element={<Profile />} />
              <Route path="page-image-gallery" element={<ImageGallery />} />
              <Route path="page-timeline" element={<Timeline />} />
              <Route path="page-pricing" element={<Pricing />} />
              <Route path="page-invoice" element={<Invoice />} />
              <Route path="page-search-result" element={<SearchResult />} />
              <Route path="page-faq" element={<Faq />} />
              <Route path="basic-tabel" element={<BasicTable />} />
              <Route path="normal-tabel" element={<NormalTable />} />
              <Route path="editable-tabel" element={<EditableTable />} />
              <Route path="tabel-color" element={<TableColor />} />
              <Route path="tabel-filter" element={<TableFilter />} />
              <Route path="apex-chart" element={<ApexChart />} />
              <Route path="knob" element={<Knob />} />
              <Route path="jvector-map" element={<JvectorMap />} />
              <Route path="yandex-map" element={<YandexMap />} />
              <Route path="quill-editor" element={<QuillEditor />} />
              <Route path="drag-drop-upload" element={<DragDropUpload />} />
              <Route path="form-validation" element={<FormValidation />} />
              <Route path="basic-elements" element={<BasicElements />} />
              <Route path="widget" element={<Widget />} />
              <Route path="documentation" element={<Documentation />} />
              <Route path="change-log" element={<ChangeLog />} />
              <Route path="data-table" element={<DataTable />} />
              <Route path="holiday" element={<Holiday />} />
              <Route path="project-dashboard" element={<ProjectDashboard />} />
              <Route path="event" element={<Event />} />
              <Route path="activity" element={<Activity />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="chat" element={<Chat />} />
              <Route path="team" element={<Team />} />
              <Route path="compose" element={<Compose />} />
              <Route path="hr-social" element={<HrSocial />} />
              <Route path="employee" element={<Employee />} />
              <Route path="employee/:id/snapshot" element={<EmployeeSnapshot />} />
              <Route path="documents" element={<EmployeeDocuments />} />
              <Route path="documents/employee/:employeeId" element={<EmployeeDocuments />} />
              <Route path="documents/upload" element={<UploadDocumentPage />} />
              <Route path="documents/:id/edit" element={<EditDocumentPage />} />
              <Route path="documents/archived" element={<ArchivedDocuments />} />
              <Route path="documents/purge-queue" element={<PurgeQueue />} />
              <Route path="add-employee" element={<AddEmployee />} />
              <Route path="edit-employee/:id" element={<AddEmployee />} />
              <Route path="edit-employee/:id" element={<AddEmployee />} />
              <Route path="leave" element={<Leave />} />
              <Route path="leave-types" element={<LeaveTypes />} />
              <Route path="leave-balance" element={<LeaveBalance />} />
              <Route path="my-leave-balance" element={<MyLeaveBalanceHR />} />
              <Route path="leave-history" element={<LeaveHistory />} />
              <Route path="my-leave-history" element={<LeaveHistory />} />
              <Route path="approvals/leave" element={<LeaveApprovals />} />
              <Route path="approvals/timesheet" element={<TimesheetApprovals />} />
              <Route path="approvals/timesheet/labor/:id" element={<LaborTimesheetApprovalDetail />} />
              <Route path="approvals/timesheet/:id" element={<TimesheetApprovalDetail />} />
              <Route path="approvals/timesheet/hr" element={<HrTimesheetApprovals />} />
              <Route path="approvals/timesheet/hr/:id" element={<HrTimesheetApprovalDetail />} />
              <Route path="approvals/kpi" element={<KPIApprovals />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="department" element={<Department />} />
              <Route path="payment" element={<Payment />} />
              <Route path="expense" element={<Expense />} />
              <Route path="invoice" element={<AccountInvoice />} />
              <Route path="payslip" element={<Payslip />} />
              <Route path="employee-salary" element={<EmployeeSalary />} />
              <Route path="payroll/template" element={<PayrollTemplate />} />
              <Route path="payroll/runs" element={<PayrollRuns />} />
              <Route path="payroll/runs/:id" element={<PayrollRunDetail />} />
              <Route path="payroll/signature" element={<PayrollSignature />} />
              <Route path="expense-report" element={<ExpenseReport />} />
              <Route path="invoice-report" element={<InvoiceReport />} />
              <Route path="user" element={<User />} />
              <Route path="add-project" element={<AddProject />} />
              <Route path="project-list" element={<ProjectList />} />
              <Route path="project-grid" element={<ProjectGrid />} />
              <Route path="project-detail" element={<ProjectDetail />} />
              <Route path="add-client" element={<AddClient />} />
              <Route path="client-list" element={<ClientList />} />
              <Route path="client-detail" element={<ClientDetail />} />
              {/* <Route path="taskboard" element={<TaskBoard />} /> */}
              <Route path="ticket" element={<Ticket />} />
              <Route path="performance/kpi" element={<KPIManagement />} />
              <Route path="performance/appraisals" element={<SelfAppraisal />} />
              <Route path="performance/360-feedback" element={<Feedback360 />} />
              <Route path="performance/360-feedback/new" element={<Feedback360New />} />
              <Route path="performance/360-feedback/request/:id" element={<Feedback360Detail />} />
              <Route path="performance/360-feedback/respond/:nominationId" element={<Feedback360Respond />} />
              <Route path="performance/360-feedback/hr" element={<Feedback360HR />} />
              <Route path="performance/reports" element={<PerformanceHistory />} />
              <Route path="timesheet" element={<Timesheet />} />
              <Route path="system-setup" element={<SystemSetup />} />
              <Route path="organization-structure" element={<OrganizationStructure />} />
              <Route path="travel" element={<Travel />} />
              <Route path="travel/:id" element={<TravelDetail />} />
              <Route path="approvals/travel" element={<TravelApprovals />} />
              <Route path="vehicle-requests" element={<VehicleRequests />} />
              <Route path="vehicle-requests/:id" element={<VehicleRequestDetail />} />
              <Route path="approvals/vehicle" element={<VehicleApprovals />} />
              <Route path="support/tickets" element={<SupportTickets />} />
              <Route path="support/tickets/new" element={<SupportTicketNew />} />
              <Route path="support/tickets/:id" element={<SupportTicketDetail />} />
              <Route path="support/queue" element={<SupportQueue />} />
              <Route path="approvals/payroll" element={<PayrollApprovals />} />
              <Route path="project-management/grant-proposal" element={<GrantProposal />} />
              <Route path="project-management/grant-proposal/new" element={<SubmitProposal />} />
              <Route path="project-management/grant-proposal/:id" element={<GrantProposalDetail />} />
              <Route path="project-management/grant-proposal/:id/redraft" element={<SubmitProposal />} />
            </Route>

            {/* Catch-all route */}
            <Route path="*" element={<Page404 />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}